'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EMPTY_ARRAY, EMPTY_OBJECT } from '#pplx/frontend/common/defaults.js';
import { sanitizedOpenWindow } from '#pplx/frontend/common/sanitizedRedirectWindow.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { DEFAULT_ATTRIBUTES, DEFAULT_VALIDATE_URL, } from '#pplx/frontend/composer-sdk/internal/link/defaults.js';
import { resolveLinkMatchers } from '#pplx/frontend/composer-sdk/internal/link/resolveLinkMatchers.js';
import { AutoLinkPlugin } from '#pplx/frontend/composer-sdk/lexical/plugins/AutoLinkPlugin.js';
import { LinkMarkdownShortcutPlugin } from '#pplx/frontend/composer-sdk/lexical/plugins/LinkMarkdownShortcutPlugin.js';
import { LinkPlugin } from '#pplx/frontend/composer-sdk/lexical/plugins/LinkPlugin.js';
import { LinkActivationType } from '#pplx/frontend/composer-sdk/link.js';
import { $isLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, isHTMLAnchorElement } from '@lexical/utils';
import { $getNearestNodeFromDOMNode, $getSelection, $isElementNode, $isRangeSelection, getNearestEditorFromDOMNode, isDOMNode, } from 'lexical';
import { useEffect, useMemo, useRef } from 'react';
function findAnchor(startNode, rootElement) {
    let node = startNode;
    while (node !== null && node !== rootElement) {
        if (isHTMLAnchorElement(node)) {
            return node;
        }
        node = node.parentNode;
    }
    return null;
}
function useStableLinkPlugins(plugins) {
    const nextLinkPlugins = plugins.filter((plugin) => plugin.link !== undefined);
    const previousLinkPluginsRef = useRef(nextLinkPlugins);
    const previousLinkPlugins = previousLinkPluginsRef.current;
    if (previousLinkPlugins.length === nextLinkPlugins.length &&
        nextLinkPlugins.every((plugin, index) => plugin === previousLinkPlugins[index])) {
        return previousLinkPlugins;
    }
    previousLinkPluginsRef.current = nextLinkPlugins;
    return nextLinkPlugins;
}
function LinkClickPlugin({ activation, onLinkClick }) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        const handleClick = (event) => {
            const target = event.target;
            if (!isDOMNode(target) || getNearestEditorFromDOMNode(target) !== editor) {
                return;
            }
            const rootElement = editor.getRootElement();
            let url = null;
            let hasSelectedLinkText = false;
            editor.update(() => {
                const clickedNode = $getNearestNodeFromDOMNode(target);
                const maybeLinkNode = clickedNode === null ? null : $findMatchingParent(clickedNode, $isElementNode);
                if ($isLinkNode(maybeLinkNode)) {
                    url = maybeLinkNode.sanitizeUrl(maybeLinkNode.getURL());
                }
                else {
                    const anchor = rootElement === null ? null : findAnchor(target, rootElement);
                    url = anchor?.href ?? null;
                }
                const selection = $getSelection();
                hasSelectedLinkText = $isRangeSelection(selection) && !selection.isCollapsed();
            });
            if (url === null || url === '') {
                return;
            }
            // Keep custom activation inert and route new-tab activation through the
            // safe opener instead of the anchor's native navigation.
            event.preventDefault();
            if (hasSelectedLinkText) {
                return;
            }
            switch (activation) {
                case LinkActivationType.NEW_TAB:
                    sanitizedOpenWindow(url);
                    break;
                case LinkActivationType.CUSTOM:
                    break;
                default:
                    assertUnreachableValue(activation);
            }
            onLinkClick?.({ url, event });
        };
        return editor.registerRootListener((rootElement, prevRootElement) => {
            prevRootElement?.removeEventListener('click', handleClick);
            rootElement?.addEventListener('click', handleClick);
        });
    }, [activation, editor, onLinkClick]);
    return null;
}
export function LinkCapabilityHost({ plugins }) {
    const linkPlugins = useStableLinkPlugins(plugins);
    const configurations = useMemo(() => linkPlugins.flatMap((plugin) => {
        if (plugin.link === undefined) {
            return [];
        }
        const configuration = plugin.link(plugin.props ?? EMPTY_OBJECT);
        return configuration.disabled ? [] : [configuration];
    }), [linkPlugins]);
    if (configurations.length > 1) {
        throw new Error(`<Composer> may mount only one active 'link' capability; received ${configurations.length}.`);
    }
    const configuration = configurations[0];
    const matchers = useMemo(() => (configuration === undefined ? undefined : resolveLinkMatchers(configuration.matchers)), [configuration]);
    if (configuration === undefined) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx(AutoLinkPlugin, { matchers: matchers, suppressIn: configuration.suppressIn ?? EMPTY_ARRAY }), _jsx(LinkPlugin, { attributes: configuration.attributes ?? DEFAULT_ATTRIBUTES, validateUrl: configuration.validateUrl ?? DEFAULT_VALIDATE_URL }), _jsx(LinkClickPlugin, { activation: configuration.activation, onLinkClick: configuration.onLinkClick }), configuration.markdownShortcut !== false ? _jsx(LinkMarkdownShortcutPlugin, {}) : null] }));
}
//# sourceMappingURL=LinkCapabilityHost.js.map